import i18n from '@/libs/i18n'
import dayjs, { formatDateTime } from '@/libs/dayjs'
import type { TagItem, SelectOption } from '@/types/Common'
import type { IReservation } from '@/types/Reservation'
import {
    ReservationStatus,
    DeviceStatus,
    UserRole,
    NotificationType,
    NotificationCategory,
    ClientType,
    QuotaRenewal,
    RecurrenceType,
    ProbeChangeStatus,
    PendingReason,
} from '@/enums/domain'

const t = (k: string, ...args: unknown[]) => i18n.global.t(k, ...(args as [])) as string

// Non-empty sentinels for select items. reka-ui's <SelectItem> forbids value=''.
// Map back to '' (omit filter) / null (no reference) at the API boundary.
export const ALL_VALUE = 'all'
export const NONE_VALUE = 'none'
// v1.7: "type the project/client by hand" option of the reservation project picker.
export const CUSTOM_VALUE = 'custom'

// --- Reservation status ---
export const getReservationStatus = (status: string): TagItem => {
    const map: Record<string, TagItem> = {
        [ReservationStatus.Confirmed]: { label: t('reservation.status.confirmed'), color: 'success' },
        [ReservationStatus.Pending]:   { label: t('reservation.status.pending'),   color: 'info' },
        [ReservationStatus.Cancelled]: { label: t('reservation.status.cancelled'), color: 'error' },
    }
    return map[status] ?? { label: status, color: 'neutral' }
}

// --- Pending reason (v1.7) ---
export const getPendingReason = (reason: string): TagItem => {
    const map: Record<string, TagItem> = {
        [PendingReason.ProbeMismatch]: { label: t('confirmation.reason.probe_mismatch'), color: 'warning' },
        [PendingReason.OutsideWindow]: { label: t('confirmation.reason.outside_window'), color: 'info' },
    }
    return map[reason] ?? { label: reason, color: 'neutral' }
}

// --- Device status ---
export const getDeviceStatus = (status: string): TagItem => {
    const map: Record<string, TagItem> = {
        [DeviceStatus.Active]:   { label: t('device.status.active'),   color: 'success' },
        [DeviceStatus.Inactive]: { label: t('device.status.inactive'), color: 'neutral' },
    }
    return map[status] ?? { label: status, color: 'neutral' }
}

// --- User role ---
export const getUserRole = (role: string): TagItem => {
    const map: Record<string, TagItem> = {
        [UserRole.SuperAdmin]: { label: t('role.super_admin'), color: 'secondary' },
        [UserRole.ProbeAdmin]: { label: t('role.probe_admin'), color: 'info' },
        [UserRole.User]:       { label: t('role.user'),        color: 'neutral' },
        [UserRole.Reporter]:   { label: t('role.reporter'),    color: 'warning' },
    }
    return map[role] ?? { label: role, color: 'neutral' }
}

// --- Client type ---
export const getClientType = (type: string): TagItem => {
    const map: Record<string, TagItem> = {
        [ClientType.Internal]: { label: t('client.type.internal'), color: 'info' },
        [ClientType.External]: { label: t('client.type.external'), color: 'warning' },
    }
    return map[type] ?? { label: type, color: 'neutral' }
}

// --- Notification type ---
export const getNotificationType = (type: string): TagItem => {
    const map: Record<string, TagItem> = {
        [NotificationType.Info]:    { label: t('notification.type.info'),    color: 'info' },
        [NotificationType.Success]: { label: t('notification.type.success'), color: 'success' },
        [NotificationType.Warning]: { label: t('notification.type.warning'), color: 'warning' },
        [NotificationType.Error]:   { label: t('notification.type.error'),   color: 'error' },
        [NotificationType.Request]: { label: t('notification.type.request'), color: 'secondary' },
    }
    return map[type] ?? { label: type, color: 'neutral' }
}

// --- Probe-change status (v1.5) ---
export const getProbeChangeStatus = (status: string, overdue = false): TagItem => {
    if (status === ProbeChangeStatus.Done) return { label: t('probe_plan.status.done'), color: 'success' }
    if (status === ProbeChangeStatus.Todo) {
        return overdue
            ? { label: t('probe_plan.status.overdue'), color: 'error' }
            : { label: t('probe_plan.status.todo'), color: 'warning' }
    }
    return { label: t('probe_plan.status.backlog'), color: 'neutral' }
}

export const getNotificationIcon = (type: string): string => {
    const map: Record<string, string> = {
        [NotificationType.Info]:    'i-lucide-info',
        [NotificationType.Success]: 'i-lucide-circle-check',
        [NotificationType.Warning]: 'i-lucide-triangle-alert',
        [NotificationType.Error]:   'i-lucide-circle-x',
        [NotificationType.Request]: 'i-lucide-repeat',
    }
    return map[type] ?? 'i-lucide-bell'
}

// --- Notification category (v1.13) ---
export const getNotificationCategory = (category: string): TagItem & { icon: string } => {
    const map: Record<string, TagItem & { icon: string }> = {
        [NotificationCategory.Reservation]: { label: t('notification.category.reservation'),  color: 'info',      icon: 'i-lucide-calendar-check' },
        [NotificationCategory.ProbeChange]: { label: t('notification.category.probe_change'), color: 'warning',   icon: 'i-lucide-repeat' },
        [NotificationCategory.Feedback]:    { label: t('notification.category.feedback'),     color: 'secondary', icon: 'i-lucide-message-square-text' },
        [NotificationCategory.Message]:     { label: t('notification.category.message'),      color: 'neutral',   icon: 'i-lucide-megaphone' },
    }
    // Rows from before v1.13 without a category read as plain messages.
    return map[category] ?? map[NotificationCategory.Message]
}

export const notificationCategoryOrder = [
    NotificationCategory.Reservation,
    NotificationCategory.ProbeChange,
    NotificationCategory.Feedback,
    NotificationCategory.Message,
]

export const renewalLabel = (renewal: string): string => {
    const map: Record<string, string> = {
        [QuotaRenewal.Weekly]:  t('quota.renewal.weekly'),
        [QuotaRenewal.Monthly]: t('quota.renewal.monthly'),
        [QuotaRenewal.Yearly]:  t('quota.renewal.yearly'),
        [QuotaRenewal.Never]:   t('quota.renewal.never'),
    }
    return map[renewal] ?? renewal
}

// --- Select option builders ---
export const reservationStatusOptions = (): SelectOption[] => [
    { label: t('reservation.status.confirmed'), value: ReservationStatus.Confirmed },
    { label: t('reservation.status.pending'),   value: ReservationStatus.Pending },
    { label: t('reservation.status.cancelled'), value: ReservationStatus.Cancelled },
]

export const deviceStatusOptions = (): SelectOption[] => [
    { label: t('device.status.active'),   value: DeviceStatus.Active },
    { label: t('device.status.inactive'), value: DeviceStatus.Inactive },
]

export const roleOptions = (): SelectOption[] => [
    { label: t('role.super_admin'), value: UserRole.SuperAdmin },
    { label: t('role.probe_admin'), value: UserRole.ProbeAdmin },
    { label: t('role.user'),        value: UserRole.User },
    { label: t('role.reporter'),    value: UserRole.Reporter },
]

export const clientTypeOptions = (): SelectOption[] => [
    { label: t('client.type.internal'), value: ClientType.Internal },
    { label: t('client.type.external'), value: ClientType.External },
]

export const renewalOptions = (): SelectOption[] => [
    { label: t('quota.renewal.weekly'),  value: QuotaRenewal.Weekly },
    { label: t('quota.renewal.monthly'), value: QuotaRenewal.Monthly },
    { label: t('quota.renewal.yearly'),  value: QuotaRenewal.Yearly },
    { label: t('quota.renewal.never'),   value: QuotaRenewal.Never },
]

export const notificationTypeOptions = (): SelectOption[] => [
    { label: t('notification.type.info'),    value: NotificationType.Info },
    { label: t('notification.type.success'), value: NotificationType.Success },
    { label: t('notification.type.warning'), value: NotificationType.Warning },
    { label: t('notification.type.error'),   value: NotificationType.Error },
]

export const recurrenceOptions = (): SelectOption[] => [
    { label: t('wizard.freq.daily'),   value: RecurrenceType.Daily },
    { label: t('wizard.freq.weekly'),  value: RecurrenceType.Weekly },
    { label: t('wizard.freq.monthly'), value: RecurrenceType.Monthly },
]

// Entity color palette (devices / groups) per mockup-analysis §7.
export const ENTITY_PALETTE = [
    '#F97066', '#FDB022', '#12B76A', '#2E90FA',
    '#9E77ED', '#EE46BC', '#F79009', '#06AED4',
]

// Progress color by percentage (green / amber ≥70 / red ≥90).
export const progressColor = (pct: number): 'success' | 'warning' | 'error' => {
    if (pct >= 90) return 'error'
    if (pct >= 70) return 'warning'
    return 'success'
}

// CSS variable for a semantic color (safe for inline :style, no Tailwind scanning needed).
export const colorVar = (color: string): string => `var(--ui-${color})`

export const progressColorVar = (pct: number): string => colorVar(progressColor(pct))

// Short group code = first word of the group name.
export const groupShortCode = (name: string | null | undefined): string => {
    if (!name) return ''
    return name.split(/[\s–-]/)[0] || name
}

// --- Reservation permissions (mirror of the backend rules; not a security boundary) ---

// Editable = not cancelled and owned by the caller. Admins may edit any reservation at
// any time, past ones included: groups often report a swapped slot, device or project
// only after the fact (v1.12). v1.13: owners edit future bookings freely and may correct
// the DATA of a started one (group, project, note — see isDataOnlyEdit) until
// `editWindowDays` after its end.
export const canEditReservation = (
    reservation: IReservation,
    userId: string | undefined,
    admin: boolean,
    editWindowDays = 0,
): boolean => {
    if (reservation.status === ReservationStatus.Cancelled) return false
    if (admin) return true
    if (reservation.user_id !== userId) return false
    if (dayjs(reservation.start_at).isAfter(dayjs())) return true
    return dayjs(reservation.end_at).add(editWindowDays, 'day').isAfter(dayjs())
}

// v1.13: an owner's edit of a reservation that has already started may only touch its
// data — the time, device and probe are fixed (the backend rejects moving it).
export const isDataOnlyEdit = (reservation: IReservation, admin: boolean): boolean =>
    !admin && !dayjs(reservation.start_at).isAfter(dayjs())

// Last moment an owner may still correct a started reservation (v1.13).
export const editDeadline = (reservation: IReservation, editWindowDays: number): string =>
    formatDateTime(dayjs(reservation.end_at).add(editWindowDays, 'day').format())

// Feedback (v1.7) is an owner action and, since v1.8, an after-use report:
// only reservations that have already ENDED qualify (the backend enforces the same).
// v1.9: one report per reservation — a stamped feedback_sent_at retires the action.
export const canSendFeedback = (reservation: IReservation, userId: string | undefined): boolean =>
    !!userId
    && reservation.user_id === userId
    && reservation.status !== ReservationStatus.Cancelled
    && !reservation.feedback_sent_at
    && !dayjs(reservation.end_at).isAfter(dayjs())

// v1.9: the "already reported" mark replacing the action once feedback was sent.
export const hasFeedbackSent = (reservation: IReservation): boolean => !!reservation.feedback_sent_at

export const feedbackSentTooltip = (reservation: IReservation): string =>
    reservation.feedback_sent_at
        ? `${t('reservation.feedback_sent_mark')} · ${formatDateTime(reservation.feedback_sent_at)}`
        : t('reservation.feedback_sent_mark')

// v1.7: a reservation shows either its linked project or the free-text entry.
export const projectDisplay = (reservation: IReservation): string =>
    reservation.project_name || reservation.custom_project || ''

// Usage-report bucket label — the empty label is the "no client" bucket.
export const clientBucketLabel = (label: string): string => label || t('report.no_client')

export const userInitials = (name: string | null | undefined): string => {
    if (!name) return '?'
    const parts = name.trim().split(/\s+/)
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}
